import { useMemo } from 'react';
import type { ChartConfig } from '@monksflow/monks-ui';
import type { PersonaDto } from '@/lib/api/personas-client';
import type { RankedItemDto } from '@inference.engine/persona-api-client/web';
import { SOCIAL_MEDIA_PLATFORM_CONFIG } from '@/shared/constants';
import type {
  ChartPlatform,
  GwiSection,
  GwiStatRow,
  MediaConsumptionData,
  SectionTableItems,
} from '../../MediaConsumptionSection.types';

// GWI platform labels that differ from SOCIAL_MEDIA_PLATFORM_CONFIG keys.
// eslint-disable-next-line i18next/no-literal-string -- lookup key into SOCIAL_MEDIA_PLATFORM_CONFIG, which resolves an icon path
const GWI_PLATFORM_ALIASES: Record<string, string> = { X: 'Twitter/X' };

const getPlatformConfig = (name: string) =>
  (SOCIAL_MEDIA_PLATFORM_CONFIG as Record<string, { color: string; image: string } | undefined>)[
    GWI_PLATFORM_ALIASES[name] ?? name
  ];

/**
 * A platform's slug in the chart config, which the donut reaches through the
 * custom property `--color-<key>`.
 *
 * A custom property name is a CSS ident, so anything outside `[a-z0-9-]` has to
 * go: "Twitter/X" spelled verbatim makes `--color-twitter/x`, which the browser
 * throws away as an invalid declaration while keeping the rest of the rule, and
 * that platform's slice is left with a fill that resolves to nothing.
 */
const toChartKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Maps a GWI section's rows to the RankedTable item shape, sorted by reach. */
const gwiRowsToRankedItems = (rows: GwiStatRow[]): RankedItemDto[] =>
  [...rows]
    .sort((a, b) => b.audiencePercentage - a.audiencePercentage)
    .map((row, idx) => ({
      rank: idx + 1,
      name: row.label,
      reach: row.audiencePercentage,
      index: row.index,
    }));

/** Sorts RankedTable items by reach, highest first. */
const sortByReach = (items: RankedItemDto[]): RankedItemDto[] =>
  [...items].sort((a, b) => b.reach - a.reach);

/**
 * Derives render-ready Media Consumption data from a persona. Prefers GWI
 * `gwiStats` when present AND the `gwi_audience_linking` flag is enabled;
 * otherwise falls back to the LLM-generated social media / media consumption
 * data. Memoized on the persona and the flag.
 *
 * @param gwiEnabled whether the `gwi_audience_linking` flag is on. When false,
 * GWI stats are ignored and the LLM fallback is always used.
 */
export function useMediaConsumptionData(
  persona: PersonaDto,
  gwiEnabled: boolean,
): MediaConsumptionData {
  return useMemo(() => {
    const platforms = persona.socialMediaData?.data.platforms ?? [];
    const mediaConsumption = persona.socialMediaData?.data.mediaConsumption;
    const gwiStats = gwiEnabled ? (persona.gwiStats ?? undefined) : undefined;
    const hasGwi = !!gwiStats && gwiStats.length > 0;

    const gwiSectionRows = (section: GwiSection): GwiStatRow[] =>
      gwiStats?.find((stat) => stat.section === section)?.rows ?? [];

    // Normalized platform list driving the pie chart + platform bars.
    const chartPlatforms: ChartPlatform[] = (
      hasGwi
        ? gwiSectionRows('Media Consumption').map((row) => {
            const name = row.platform ?? row.label;
            return {
              name,
              percentage: row.audiencePercentage,
              index: row.index,
              color: getPlatformConfig(name)?.color ?? '#888',
              image: getPlatformConfig(name)?.image,
              description: row.label,
            };
          })
        : platforms.map((p) => ({
            name: p.platformName,
            percentage: p.usageLikelihood,
            index: p.affinityIndex,
            color: getPlatformConfig(p.platformName)?.color ?? '#888',
            image: getPlatformConfig(p.platformName)?.image,
            description: p.shortDescription,
          }))
    ).sort((a, b) => b.percentage - a.percentage);

    // Per-section RankedTable items, GWI-or-LLM, sorted by reach.
    const tableItems: SectionTableItems = {
      Platforms: hasGwi
        ? gwiRowsToRankedItems(gwiSectionRows('Platforms'))
        : sortByReach(mediaConsumption?.topPlatforms ?? []),
      'Content Types': hasGwi
        ? gwiRowsToRankedItems(gwiSectionRows('Content Types'))
        : sortByReach(mediaConsumption?.topContentTypes ?? []),
      Influencers: hasGwi
        ? gwiRowsToRankedItems(gwiSectionRows('Influencers'))
        : sortByReach(mediaConsumption?.topInfluencers ?? []),
      Channels: hasGwi
        ? gwiRowsToRankedItems(gwiSectionRows('Channels'))
        : sortByReach(mediaConsumption?.topChannels ?? []),
      'Media Consumption': hasGwi ? gwiRowsToRankedItems(gwiSectionRows('Media Consumption')) : [],
    };

    const chartConfig = chartPlatforms.reduce<ChartConfig>((acc, platform) => {
      acc[toChartKey(platform.name)] = { label: platform.name, color: platform.color };
      return acc;
    }, {});

    const chartData = chartPlatforms.map((platform) => {
      const key = toChartKey(platform.name);
      return { platform: key, value: platform.percentage, fill: `var(--color-${key})` };
    });

    return {
      chartPlatforms,
      chartConfig,
      chartData,
      tableItems,
      hasGwiSection: (section: GwiSection) => gwiSectionRows(section).length > 0,
      gwiSectionRows,
    };
  }, [persona, gwiEnabled]);
}
